import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, ArrowRight, ArrowLeft } from "@phosphor-icons/react";
import Logo from "@/components/Logo";

/**
 * Generic portal-login component. Three variants:
 * - kind="hr"     → /hr     · for hr_manager / ops_coordinator / accountant / supervisor / managing_director
 * - kind="staff"  → /staff  · for office_staff / collection_staff
 * - kind="admin"  → /adentry · sacred · only super_admin
 */
const ROLE_GATE = {
  hr:    new Set(["hr_manager", "ops_coordinator", "accountant", "supervisor", "managing_director"]),
  staff: new Set(["office_staff", "collection_staff"]),
  admin: new Set(["super_admin"]),
};

const COPY = {
  hr:    { eyebrow: "HR Portal",     title: "Welcome back.",     sub: "Sign in to manage your workforce.",
           cta: "Open HR portal",    redirect: "/dashboard",
           denial: "This account isn't authorised for the HR portal." },
  staff: { eyebrow: "Staff App",     title: "Hi there.",         sub: "Sign in to punch attendance, apply leave and view salary.",
           cta: "Open staff app",    redirect: "/home",
           denial: "This account is for HR. Please use the HR portal." },
  admin: { eyebrow: "Restricted",    title: "Administrator entry.",
           sub: "This portal is reserved. Unauthorised access is logged.",
           cta: "Continue",          redirect: "/dashboard",
           denial: "This account is not authorised for administrator entry." },
};

export default function PortalLogin({ kind }) {
  const { login, logout } = useAuth();
  const nav = useNavigate();
  const [ident, setIdent] = useState("");
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const copy = COPY[kind];

  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      const u = await login(ident.trim(), pw);
      const allowed = ROLE_GATE[kind].has(u.role);
      if (!allowed) {
        await logout();
        setErr(copy.denial);
        toast.error(copy.denial);
        return;
      }
      toast.success("Signed in");
      nav(copy.redirect, { replace: true });
    } catch (e2) {
      const msg = formatApiError(e2);
      setErr(msg); toast.error(msg);
    } finally { setLoading(false); }
  }

  const isAdmin = kind === "admin";

  return (
    <div className="min-h-screen flex bg-[#0D0D10] text-white">
      {/* Left artwork — hidden on admin variant for a "sterile" feel */}
      {!isAdmin && (
        <div className="hidden lg:flex w-1/2 relative overflow-hidden border-r border-[#2D2D35]">
          <div className="absolute inset-0"
               style={{ background:
                 "radial-gradient(ellipse at 25% 30%, rgba(229,9,20,0.25), transparent 60%)," +
                 "linear-gradient(135deg, #0D0D10, #15050a 60%, #0D0D10)" }} />
          <div className="absolute inset-0 opacity-[0.06]"
               style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
                        backgroundSize: "32px 32px" }} />
          <div className="relative z-10 p-12 xl:p-16 flex flex-col justify-between w-full">
            <Logo />
            <div>
              <p className="text-[10px] tracking-[0.4em] uppercase text-[#E50914] mb-4">{copy.eyebrow}</p>
              <h1 className="font-display text-5xl xl:text-6xl tracking-tight leading-[0.95]">
                Workforce<br/>intelligence,<br/>
                <span className="text-[#E50914]">re-engineered.</span>
              </h1>
              <p className="text-zinc-400 mt-6 max-w-md leading-relaxed">
                Attendance, payroll, leave & geo-fence — every employee, every shift, every minute.
              </p>
            </div>
            <p className="text-xs text-zinc-500 tracking-widest uppercase">EthicHR · v2.0</p>
          </div>
        </div>
      )}

      <div className={`flex-1 flex items-center justify-center p-6 ${isAdmin ? "" : ""}`}>
        <form onSubmit={onSubmit} className={`w-full ${isAdmin ? "max-w-xs" : "max-w-sm"} rise`}
              data-testid={`login-form-${kind}`}>
          <div className="lg:hidden mb-8"><Logo /></div>

          {isAdmin ? (
            <div className="flex items-center gap-2 text-[10px] tracking-[0.4em] uppercase text-[#FF3B30] mb-3">
              <ShieldCheck size={12} weight="fill" /> {copy.eyebrow}
            </div>
          ) : (
            <Link to="/" className="inline-flex items-center gap-1 text-[11px] text-zinc-500 hover:text-white mb-4"
                  data-testid={`back-${kind}`}>
              <ArrowLeft size={12} /> Home
            </Link>
          )}

          <p className="text-xs tracking-[0.3em] uppercase text-[#E50914] mb-3">{isAdmin ? "Authorised personnel" : "Sign in"}</p>
          <h2 className="font-display text-3xl mb-2">{copy.title}</h2>
          <p className="text-sm text-zinc-400 mb-8">{copy.sub}</p>

          <div className="space-y-4">
            <div>
              <Label htmlFor="ident" className="text-xs uppercase tracking-widest text-zinc-500">Email / Employee ID</Label>
              <Input id="ident" data-testid="login-identifier-input"
                     value={ident} onChange={(e) => setIdent(e.target.value)} placeholder="Enter email"
                     className="mt-2 bg-[#16161C] border-[#2D2D35] focus:border-[#E50914] focus:ring-1 focus:ring-[#E50914] h-11" required />
            </div>
            <div>
              <Label htmlFor="pw" className="text-xs uppercase tracking-widest text-zinc-500">Password</Label>
              <Input id="pw" type="password" data-testid="login-password-input"
                     value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Enter password"
                     className="mt-2 bg-[#16161C] border-[#2D2D35] focus:border-[#E50914] focus:ring-1 focus:ring-[#E50914] h-11" required />
            </div>
            {err && <p className="text-xs text-[#FF3B30]" data-testid="login-error">{err}</p>}
            <Button type="submit" data-testid="login-submit-button" disabled={loading}
                    className="w-full h-11 bg-[#E50914] hover:bg-[#C80812] text-white rounded-md font-semibold tracking-wide">
              {loading ? "Signing in..." : <span className="inline-flex items-center gap-2">{copy.cta} <ArrowRight size={16} weight="bold" /></span>}
            </Button>
          </div>

          <div className="mt-8 flex items-center gap-2 text-xs text-zinc-500">
            <ShieldCheck size={14} className="text-[#E50914]" />
            <span>JWT secured · HTTPS · Audit-logged</span>
          </div>
        </form>
      </div>
    </div>
  );
}
